/**
 * Background export jobs (TZ 8.9.2): POST /exports starts a job, the panel polls GET /exports/{id} until it is
 * done, then offers the download link (valid 24 h, only for this user). Jobs keep running while the user
 * navigates; the header shows a small indicator. A downloaded export leaves the lists (owner decision 2026-10-05).
 */
import { useToast } from '@person-platform/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useEndpoints } from '../../api/api_context';
import type { ExportJob } from '../../api/api_types';
import { EXPORT_POLL_INTERVAL_MS } from '../../api/queries/workspace_queries';
import { queryKeys } from '../../api/query_keys';
import { useClientTranslation } from '../../i18n/i18n_setup';
import { exportLanguageOf, toExportRequest, type ExportOrder } from './export_filters';
import {
  ExportJobsContext,
  exportDownloadUrl,
  isFinishedExport,
  openDownload,
  type DownloadableExport,
  type TrackedExportJob,
} from './export_jobs_context';

/** The server marks an export downloaded after the last byte; the list is refreshed once that has happened. */
const LIST_REFRESH_AFTER_DOWNLOAD_MS = 4_000;

export function ExportJobsProvider({ children }: { children: ReactNode }) {
  const endpoints = useEndpoints();
  const { showToast } = useToast();
  const { t: translate, i18n } = useClientTranslation();
  const queryClient = useQueryClient();
  const [jobs, setJobs] = useState<TrackedExportJob[]>([]);
  const [downloadedExportIds, setDownloadedExportIds] = useState<ReadonlySet<string>>(() => new Set());
  const jobsRef = useRef(jobs);

  const refreshExportList = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.exportList });
  }, [queryClient]);

  const downloadExport = useCallback(
    (exportJob: DownloadableExport, fileName: string) => {
      openDownload(exportDownloadUrl(exportJob), fileName);
      setJobs((currentJobs) => currentJobs.filter((trackedJob) => trackedJob.job.id !== exportJob.id));
      setDownloadedExportIds((currentIds) => new Set([...currentIds, exportJob.id]));
      window.setTimeout(refreshExportList, LIST_REFRESH_AFTER_DOWNLOAD_MS);
    },
    [refreshExportList],
  );

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const notifyFinished = useCallback(
    (trackedJob: TrackedExportJob) => {
      if (trackedJob.job.status === 'done') {
        showToast({
          tone: 'good',
          title: translate('exports.ready_title'),
          description: trackedJob.label,
          durationMs: 15_000,
          action: {
            label: translate('exports.download'),
            onSelect: () => {
              downloadExport(trackedJob.job, trackedJob.job.file_name ?? trackedJob.label);
            },
          },
        });
      } else {
        showToast({ tone: 'bad', title: translate('exports.failed_title'), description: trackedJob.label });
      }
    },
    [showToast, translate, downloadExport],
  );

  const hasRunningJobs = jobs.some((trackedJob) => !isFinishedExport(trackedJob.job));

  useEffect(() => {
    if (!hasRunningJobs) {
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      for (const trackedJob of jobsRef.current.filter((candidate) => !isFinishedExport(candidate.job))) {
        endpoints.exports
          .status(trackedJob.job.id)
          .then((updatedJob) => {
            setJobs((currentJobs) =>
              currentJobs.map((currentJob) =>
                currentJob.job.id === updatedJob.id ? { ...currentJob, job: updatedJob } : currentJob,
              ),
            );
            if (isFinishedExport(updatedJob)) {
              notifyFinished({ ...trackedJob, job: updatedJob });
              refreshExportList();
            }
          })
          .catch((pollError: unknown) => {
            console.error('Export status polling failed', pollError);
          });
      }
    }, EXPORT_POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasRunningJobs, endpoints, notifyFinished, refreshExportList]);

  const startExport = useCallback(
    async (exportOrder: ExportOrder, label: string) => {
      try {
        const createdJob = await endpoints.exports.create(
          toExportRequest(exportOrder, exportLanguageOf(i18n.language)),
        );
        const queuedJob: ExportJob = {
          ...createdJob,
          download_url: null,
          expires_at: null,
          file_name: null,
          failure_reason: null,
        };
        setJobs((currentJobs) =>
          [{ job: queuedJob, label, startedAt: Date.now() }, ...currentJobs].slice(0, 10),
        );
        showToast({ tone: 'info', title: translate('exports.started_title'), description: label });
        refreshExportList();
      } catch (createError) {
        console.error('Export could not be started', createError);
        showToast({ tone: 'bad', title: translate('exports.failed_title'), description: label });
      }
    },
    [endpoints, i18n, showToast, translate, refreshExportList],
  );

  const dismissJob = useCallback((jobId: string) => {
    setJobs((currentJobs) => currentJobs.filter((trackedJob) => trackedJob.job.id !== jobId));
  }, []);

  const contextValue = useMemo(
    () => ({ jobs, startExport, dismissJob, downloadExport, downloadedExportIds }),
    [jobs, startExport, dismissJob, downloadExport, downloadedExportIds],
  );
  return <ExportJobsContext.Provider value={contextValue}>{children}</ExportJobsContext.Provider>;
}
